import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link, useParams } from '@tanstack/react-router';
import { Banner, DataTable, EmptyState, Heading, LoadingOrbit, Page, PageHeader, TagBadge, Text } from '@chatsdet/ui';
import { Gauge } from '@phosphor-icons/react';
import type { PerfSlowSpan } from '@chatsdet/types/perf';
import { runVerdictPresentation, sortRows } from '../../../features/perf/perfPresentation';
import { ROW_COLUMNS } from '../../../features/perf/perfRowColumns';
import { ApiError } from '../../../lib/api-error';
import { usePerfRun } from '../../../features/perf/usePerf';

const SPAN_COLUMNS: ColumnDef<StockFeatures, PerfSlowSpan>[] = [
  {
    id: 'name',
    header: 'Name',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="font-mono truncate">{row.original.name}</Text>,
    meta: { flex: 3, minWidth: 220 },
  },
  {
    id: 'count',
    header: 'Count',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{row.original.count}</Text>,
    meta: { minWidth: 80 },
  },
  {
    id: 'p95',
    header: 'Worst round p95',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{`${Number(row.original.p95Ms.toFixed(1))} ms`}</Text>,
    meta: { minWidth: 140 },
  },
  {
    id: 'total',
    header: 'Total duration',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm" className="tabular-nums">{`${Number(row.original.totalMs.toFixed(1))} ms`}</Text>,
    meta: { minWidth: 140 },
  },
];

function SpanTable({ title, spans }: { title: string; spans: PerfSlowSpan[] }) {
  return (
    <section className="space-y-2">
      <Heading level={4}>{title}</Heading>
      {spans.length === 0 ? (
        <Text size="sm" className="text-muted-foreground">No server spans.</Text>
      ) : (
        <DataTable
          columns={SPAN_COLUMNS}
          data={spans}
          aria-label={`${title} table`}
          getRowId={(span) => span.name}
        />
      )}
    </section>
  );
}

const Sha = ({ label, sha }: { label: string; sha: string }) => (
  <span>
    {label} <span className="font-mono text-foreground">{sha}</span>
  </span>
);

export function PerfRunPage() {
  const { runId } = useParams({ from: '/performance/runs/$runId' });
  const query = usePerfRun(runId);
  const run = query.data;

  if (query.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }
  if (!run && query.isError && !(query.error instanceof ApiError && query.error.status === 404)) {
    return (
      <Page>
        <Banner variant="error">{query.error.message}</Banner>
      </Page>
    );
  }
  if (!run) {
    return (
      <Page>
        <EmptyState icon={<Gauge size={48} />} title="Run not found" description="This performance run does not exist." />
      </Page>
    );
  }

  const badge = runVerdictPresentation(run);
  const result = run.result;
  const tools = result
    ? [
        result.tools.lighthouse && `lighthouse ${result.tools.lighthouse}`,
        result.tools.k6 && `k6 ${result.tools.k6}`,
        result.tools.otelcol && `otelcol-contrib ${result.tools.otelcol}`,
      ].filter((tool): tool is string => Boolean(tool))
    : [];

  return (
    <Page>
      <PageHeader
        icon={<Gauge size={24} weight="fill" />}
        title="Performance run"
        eyebrow={<TagBadge name={badge.label} color={badge.color} size="md" />}
        description={
          <span className="flex flex-wrap gap-x-4">
            <Sha label="Head" sha={run.headSha} />
            <Sha label="Base" sha={run.baseSha} />
            {result && <span>{result.rounds} rounds</span>}
          </span>
        }
      />
      {run.perfTestId ? (
        <Link to="/performance/tests/$testId" params={{ testId: run.perfTestId }} className="text-sm font-medium text-foreground hover:underline">
          Back to test
        </Link>
      ) : (
        <Link to="/performance" className="text-sm font-medium text-foreground hover:underline">
          All performance tests
        </Link>
      )}
      {run.status === 'failed' && <Banner variant="error">{run.error ?? 'The run failed.'}</Banner>}
      {!result ? (
        <EmptyState
          icon={<Gauge size={48} />}
          title={run.status === 'failed' ? 'No results' : 'Measuring'}
          description={run.status === 'failed' ? 'The run ended before producing results.' : 'Results appear when the run completes.'}
        />
      ) : (
        <>
          <DataTable
            columns={ROW_COLUMNS}
            data={sortRows(result.rows)}
            aria-label="Verdict table"
            getRowId={(row) => row.id}
          />
          <Text size="sm" className="text-muted-foreground">
            Tools: {tools.length > 0 ? tools.join(', ') : 'none'}
          </Text>
          {result.otel && (
            <>
              <Heading level={3}>Slowest server spans</Heading>
              <SpanTable title="Head" spans={result.otel.topSlowSpans.head} />
              <SpanTable title="Base" spans={result.otel.topSlowSpans.base} />
            </>
          )}
        </>
      )}
    </Page>
  );
}
